"use client";
import { Button } from "../../elements/button/button";
import { DialogFooter } from "../../elements/dialog/dialog";
import { IconLoader2, IconTrash } from "../../icons";
import type { ButtonVariant } from "./generic-modal";

type ModalFooterProps = {
  primaryButtonText?: string;
  isPrimaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  secondaryButtonVariant?: ButtonVariant;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  deleteButtonText?: string;
  isLoading?: boolean;
  tertiaryButtonText?: string;
  onTertiaryAction?: () => void;
  isTertiaryButtonDisabled?: boolean;
  tertiaryButtonVariant?: ButtonVariant;
  formAttributes?: Record<string, string> | {};
  formId?: string;
  onClose: () => void;
};

export function ModalFooter(props: Readonly<ModalFooterProps>) {
  if (
    !(
      props.primaryButtonText ||
      props.secondaryButtonText ||
      props.showDeleteButton
    )
  ) {
    return null;
  }

  return (
    <DialogFooter>
      <div className="flex w-full flex-col items-center justify-between gap-3 px-2 sm:flex-row sm:items-center">
        {props.showDeleteButton ? (
          <div className="flex w-full items-center justify-center sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
            <Button
              disabled={!!props.isLoading}
              onClick={props.onDelete}
              variant="destructive"
            >
              <IconTrash size="sm" />
              {props.deleteButtonText}
            </Button>
          </div>
        ) : (
          //  to keep the flex layout consistent when the delete button is not shown
          <div />
        )}

        <div className="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row">
          {props.secondaryButtonText ? (
            <div className="w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                disabled={!!props.isLoading}
                onClick={props.onSecondaryAction || props.onClose}
                variant={props.secondaryButtonVariant ?? "outline"}
              >
                {props.secondaryButtonText}
              </Button>
            </div>
          ) : null}

          {props.tertiaryButtonText ? (
            <div className="w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                disabled={!!props.isLoading || !!props.isTertiaryButtonDisabled}
                onClick={props.onTertiaryAction}
                variant={props.tertiaryButtonVariant || "default"}
              >
                {props.tertiaryButtonText}
              </Button>
            </div>
          ) : null}

          {props.primaryButtonText ? (
            <div className="relative w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                {...(props.formAttributes ?? {})}
                disabled={!!props.isPrimaryButtonDisabled || !!props.isLoading}
                onClick={props.onPrimaryAction}
                type={props.formId ? "submit" : "button"}
                variant="default"
              >
                <span className={props.isLoading ? "invisible" : ""}>
                  {props.primaryButtonText}
                </span>

                {props.isLoading ? (
                  <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
                    <IconLoader2 className="animate-spin" size="md" />
                  </span>
                ) : null}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </DialogFooter>
  );
}
